import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { categorizeCardExpense, categorizeUnifiedExpense, CARD_EXPENSE_OVERHEAD } from './cardExpensesApi';
import {
  CARD_PO_MATCH_TARGET_OVERHEAD,
  categorizationFromPurchaseOrder,
  matchCardExpenseToPurchaseOrder,
  type CardPoMatchExpense,
  type CardPoMatchRequisition,
} from './cardExpensePurchaseMatch';
import { PURCHASE_REQUISITIONS_COLLECTION } from './purchasingRequisitions';
import { qboVendorBillAppUrl } from './purchaseQbBillStatus';
import type { PurchaseRequisition } from '../types/purchasing';
import type { QBExpense, ProjectPLBucket } from '../types';
import {
  buildCardPoAutoPurchasePatch,
  type LinkedCardChargeLite,
} from './cardPoAutoPurchase';
import {
  appendLinkedExpenseId,
  attributeChargesToPurchaseOrder,
  normalizeLinkedExpenseIds,
  removeLinkedExpenseId,
  type PurchaseOrderSplitLite,
} from './cardPoLinkedCharges';

function toMatchExpense(expense: QBExpense & { id: string }): CardPoMatchExpense {
  return {
    id: expense.id,
    vendorName: expense.vendorName,
    description: expense.description,
    totalAmount: expense.totalAmount,
    txnDate: expense.txnDate,
    assignedUserId: expense.assignedUserId,
    categorizationStatus: expense.categorizationStatus,
    purchaseRequisitionId: (expense as { purchaseRequisitionId?: string }).purchaseRequisitionId,
  };
}

function toMatchRequisition(row: PurchaseRequisition): CardPoMatchRequisition {
  return {
    id: row.id,
    poNumber: row.poNumber,
    status: row.status,
    vendorName: row.vendorName,
    title: row.title,
    amount: row.amount,
    purchasedAmount: row.purchasedAmount,
    paymentMethodId: row.paymentMethodId,
    paymentMethod: row.paymentMethod,
    requesterId: row.requesterId,
    purchasedById: row.purchasedById,
    spendClass: row.spendClass,
    dealId: row.dealId,
    plBucket: row.plBucket,
    costObject: row.costObject,
    purchasedAt: row.purchasedAt,
    approvedAt: row.approvedAt,
    createdAt: row.createdAt,
    qbExpenseId: row.qbExpenseId,
    qbExpenseIds: row.qbExpenseIds,
    prNumber: row.prNumber,
  };
}

export function chargeLiteFromExpense(expense: QBExpense & { id: string }): LinkedCardChargeLite {
  return {
    id: expense.id,
    totalAmount: expense.totalAmount,
    txnDate: expense.txnDate,
    cardLastFour: (expense as { cardLastFour?: string }).cardLastFour,
    bankAccountName: expense.bankAccountName,
  };
}

type LinkedChargeWithSplits = LinkedCardChargeLite & {
  purchaseOrderSplits?: PurchaseOrderSplitLite[] | null;
};

async function loadLinkedChargeLites(expenseIds: string[]): Promise<LinkedChargeWithSplits[]> {
  const charges: LinkedChargeWithSplits[] = [];
  for (const id of expenseIds) {
    const snap = await getDoc(doc(db, 'qbExpenses', id));
    if (!snap.exists()) continue;
    const data = snap.data() || {};
    const splits = Array.isArray(data.purchaseOrderSplits)
      ? (data.purchaseOrderSplits as PurchaseOrderSplitLite[])
      : null;
    charges.push({
      id,
      totalAmount: Number(data.totalAmount) || 0,
      txnDate: typeof data.txnDate === 'string' ? data.txnDate : null,
      cardLastFour: typeof data.cardLastFour === 'string' ? data.cardLastFour : null,
      bankAccountName: typeof data.bankAccountName === 'string' ? data.bankAccountName : null,
      purchaseOrderSplits: splits,
    });
  }
  return charges;
}

async function loadExpenseToPoIds(expenseIds: string[]): Promise<Map<string, string[]>> {
  const map = new Map<string, string[]>();
  await Promise.all(
    expenseIds.map(async expenseId => {
      const id = expenseId.trim();
      if (!id) return;
      const col = collection(db, PURCHASE_REQUISITIONS_COLLECTION);
      const [byArray, bySingular] = await Promise.all([
        getDocs(query(col, where('qbExpenseIds', 'array-contains', id))),
        getDocs(query(col, where('qbExpenseId', '==', id))),
      ]);
      const ids = new Set<string>();
      byArray.forEach(snap => ids.add(snap.id));
      bySingular.forEach(snap => ids.add(snap.id));
      map.set(id, Array.from(ids).sort());
    }),
  );
  return map;
}

/**
 * Recompute purchasedAmount from linked charges with shared-charge attribution
 * (purchaseOrderSplits amount, else even split across POs that list the charge).
 * Skips writing when there are no valid linked charges (keeps existing amount).
 */
export async function recomputeClientPurchasedAmountFromLinkedCharges(args: {
  requisitionId: string;
  actorId: string;
  actorName: string;
  /** Optional in-memory requisition for auto-purchase eligibility before refresh. */
  requisition?: PurchaseRequisition;
}): Promise<number | null> {
  const poRef = doc(db, PURCHASE_REQUISITIONS_COLLECTION, args.requisitionId);
  const poSnap = await getDoc(poRef);
  if (!poSnap.exists()) return null;
  const data = poSnap.data() || {};
  const linkedIds = normalizeLinkedExpenseIds(data.qbExpenseIds, data.qbExpenseId);
  if (!linkedIds.length) return null;

  const charges = await loadLinkedChargeLites(linkedIds);
  if (!charges.length) return null;

  const expenseToPoIds = await loadExpenseToPoIds(charges.map(c => c.id));
  if (!expenseToPoIds.get(charges[0].id)?.includes(args.requisitionId)) {
    // Ensure current PO is counted even if queries race after a fresh link.
    for (const charge of charges) {
      const prior = expenseToPoIds.get(charge.id) || [];
      if (!prior.includes(args.requisitionId)) {
        expenseToPoIds.set(charge.id, [...prior, args.requisitionId].sort());
      }
    }
  }

  const attributed = attributeChargesToPurchaseOrder({
    purchaseRequisitionId: args.requisitionId,
    charges: charges.map(c => ({
      id: c.id,
      totalAmount: c.totalAmount,
      purchaseOrderSplits: c.purchaseOrderSplits,
    })),
    expenseToPoIds,
  });
  const purchasedAmount = attributed.total;
  const attributedCharges: LinkedCardChargeLite[] = charges.map(c => {
    const row = attributed.attributions.find(a => a.expenseId === c.id);
    return { ...c, totalAmount: row ? row.amountForThisPo : Number(c.totalAmount) || 0 };
  });
  const status = String(data.status || args.requisition?.status || '');

  if (status === 'purchased') {
    await updateDoc(poRef, {
      purchasedAmount,
      updatedAt: serverTimestamp(),
    });
    return purchasedAmount;
  }

  const requisitionForPatch: PurchaseRequisition = {
    ...(args.requisition || ({ id: args.requisitionId } as PurchaseRequisition)),
    id: args.requisitionId,
    status: (status || 'approved') as PurchaseRequisition['status'],
    amount: Number(data.amount ?? args.requisition?.amount) || 0,
    paymentMethodId:
      (typeof data.paymentMethodId === 'string' ? data.paymentMethodId : null) ||
      args.requisition?.paymentMethodId,
    paymentMethod:
      (typeof data.paymentMethod === 'string' ? data.paymentMethod : null) ||
      args.requisition?.paymentMethod,
  };
  const patch = buildCardPoAutoPurchasePatch({
    po: requisitionForPatch,
    charges: attributedCharges,
    actorId: args.actorId,
    actorName: args.actorName,
  });
  if (!patch) {
    if (linkedIds.length > 0) {
      await updateDoc(poRef, { purchasedAmount, updatedAt: serverTimestamp() });
      return purchasedAmount;
    }
    return null;
  }
  await updateDoc(poRef, {
    status: patch.status,
    purchasedAmount: patch.purchasedAmount,
    purchasedAt: Timestamp.fromDate(patch.purchasedAt),
    purchasedById: patch.purchasedById,
    purchasedByName: patch.purchasedByName,
    ...(patch.purchaseVariance ? { purchaseVariance: patch.purchaseVariance } : {}),
    ...(patch.cardLastFour ? { cardLastFour: patch.cardLastFour } : {}),
    updatedAt: serverTimestamp(),
  });
  return patch.purchasedAmount;
}

export async function applyCardPoAutoPurchaseIfNeeded(args: {
  requisition: PurchaseRequisition;
  charges: LinkedCardChargeLite[];
  actorId: string;
  actorName: string;
}): Promise<boolean> {
  // Always recompute from the full linked set after a link — never first-charge only.
  const amount = await recomputeClientPurchasedAmountFromLinkedCharges({
    requisitionId: args.requisition.id,
    actorId: args.actorId,
    actorName: args.actorName,
    requisition: args.requisition,
  });
  return amount != null;
}

export function toCardPoMatchRequisition(row: PurchaseRequisition): CardPoMatchRequisition {
  return toMatchRequisition(row);
}

/**
 * Atomically append expenseId to qbExpenseIds (deduped) and stamp the expense.
 * purchasedAmount is recomputed separately from the full linked set.
 */
export async function stampCardExpensePurchaseLink(
  expenseId: string,
  requisition: Pick<PurchaseRequisition, 'id' | 'poNumber'> & { sequenceTransactionId?: string },
  sequenceTransactionId?: string | null,
): Promise<string[]> {
  const poRef = doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisition.id);
  const expenseRef = doc(db, 'qbExpenses', expenseId);

  const nextIds = await runTransaction(db, async tx => {
    const poSnap = await tx.get(poRef);
    const poData = poSnap.exists() ? poSnap.data() || {} : {};
    const existing = normalizeLinkedExpenseIds(poData.qbExpenseIds, poData.qbExpenseId);
    const linked = appendLinkedExpenseId(existing, expenseId);

    tx.set(
      expenseRef,
      {
        purchaseRequisitionId: requisition.id,
        poNumber: requisition.poNumber || null,
        categorizedVia: 'purchase_order',
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );

    const poPatch: Record<string, unknown> = {
      qbExpenseId: expenseId,
      qbExpenseIds: linked,
      updatedAt: serverTimestamp(),
    };
    if (sequenceTransactionId) {
      const seqIds = normalizeLinkedExpenseIds(
        poData.sequenceTransactionIds,
        poData.sequenceTransactionId,
      );
      poPatch.sequenceTransactionId = sequenceTransactionId;
      poPatch.sequenceTransactionIds = appendLinkedExpenseId(seqIds, sequenceTransactionId);
    }
    tx.set(poRef, poPatch, { merge: true });
    return linked;
  });

  return nextIds;
}

/** Manual inbox map — no penny/vendor uniqueness required. Several charges may share one PO. */
export async function categorizeCardExpenseToPurchaseOrder(params: {
  expense: QBExpense & { id: string };
  requisition: PurchaseRequisition;
  actorId?: string;
  actorName?: string;
}): Promise<string> {
  const dest = categorizationFromPurchaseOrder(toMatchRequisition(params.requisition));
  const target = dest.target === CARD_PO_MATCH_TARGET_OVERHEAD ? CARD_EXPENSE_OVERHEAD : dest.target;
  await categorizeCardExpense({
    expenseId: params.expense.id,
    target,
    dealTitle: dest.dealTitle,
    plBucket: dest.plBucket as ProjectPLBucket,
    plBucketSource: 'auto',
  });
  await stampCardExpensePurchaseLink(
    params.expense.id,
    params.requisition,
    params.expense.sequenceTransactionId,
  );
  await recomputeClientPurchasedAmountFromLinkedCharges({
    requisitionId: params.requisition.id,
    actorId: params.actorId || 'system-card-link',
    actorName: params.actorName || 'CRM',
    requisition: params.requisition,
  }).catch(() => undefined);
  return params.requisition.poNumber || params.requisition.prNumber || params.requisition.id;
}

export async function categorizeCardExpenseFromPurchaseOrder(params: {
  expense: QBExpense & { id: string };
  requisitions: PurchaseRequisition[];
  actorId?: string;
  actorName?: string;
}): Promise<string | null> {
  const match = matchCardExpenseToPurchaseOrder(
    toMatchExpense(params.expense),
    params.requisitions.map(toMatchRequisition),
  );
  if (!match) return null;

  const target = match.target === CARD_EXPENSE_OVERHEAD ? CARD_EXPENSE_OVERHEAD : match.target;
  await categorizeCardExpense({
    expenseId: params.expense.id,
    target,
    dealTitle: match.dealTitle,
    plBucket: match.plBucket as ProjectPLBucket,
    plBucketSource: 'auto',
  });
  await stampCardExpensePurchaseLink(
    params.expense.id,
    match.requisition,
    params.expense.sequenceTransactionId,
  );
  const full = params.requisitions.find(r => r.id === match.requisition.id);
  await recomputeClientPurchasedAmountFromLinkedCharges({
    requisitionId: match.requisition.id,
    actorId: params.actorId || 'system-card-link',
    actorName: params.actorName || 'CRM',
    requisition: full,
  }).catch(() => undefined);
  return match.requisition.poNumber || match.requisition.id;
}

export async function stampHeuristicPoQbAscription(args: {
  expenseId: string;
  requisition: Pick<PurchaseRequisition, 'id' | 'poNumber'> & { qbBillId?: string | null };
  entityType?: string | null;
  quickbooksId?: string | null;
  sequenceTransactionId?: string | null;
}): Promise<void> {
  await stampCardExpensePurchaseLink(args.expenseId, args.requisition, args.sequenceTransactionId);
  const qbId = (args.quickbooksId || '').trim();
  if (args.entityType === 'bill' && qbId && !(args.requisition.qbBillId || '').trim()) {
    await updateDoc(doc(db, PURCHASE_REQUISITIONS_COLLECTION, args.requisition.id), {
      qbBillStatus: 'linked',
      qbBillId: qbId,
      qbBillUrl: qboVendorBillAppUrl(qbId),
      qbBillMessage: 'Linked to an existing QuickBooks bill.',
      updatedAt: serverTimestamp(),
    });
  }
}

/** Map any QB expense (card charge or vendor bill) onto an issued PO. No QBO write-back. */
export async function linkQbExpenseToPurchaseOrder(params: {
  expense: {
    id: string;
    entityType?: QBExpense['entityType'] | null;
    quickbooksId?: string | null;
    sequenceTransactionId?: string | null;
    purchaseRequisitionId?: string | null;
    totalAmount?: number | null;
    txnDate?: string | null;
    cardLastFour?: string | null;
    bankAccountName?: string | null;
  };
  requisition: PurchaseRequisition;
  actorId?: string;
  actorName?: string;
  charge?: LinkedCardChargeLite;
}): Promise<string> {
  const dest = categorizationFromPurchaseOrder(toMatchRequisition(params.requisition));
  const target = dest.target === CARD_PO_MATCH_TARGET_OVERHEAD ? CARD_EXPENSE_OVERHEAD : dest.target;
  const existingPo = (params.expense.purchaseRequisitionId || '').trim();
  if (existingPo && existingPo !== params.requisition.id) {
    await unlinkQbExpenseFromPurchaseOrder({
      expenseId: params.expense.id,
      purchaseRequisitionId: existingPo,
    });
  }
  await categorizeUnifiedExpense({
    expenseId: params.expense.id,
    target,
    dealTitle: dest.dealTitle,
    plBucket: dest.plBucket as ProjectPLBucket,
    plBucketSource: 'auto',
  });
  await stampHeuristicPoQbAscription({
    expenseId: params.expense.id,
    requisition: params.requisition,
    entityType: params.expense.entityType,
    quickbooksId: params.expense.quickbooksId,
    sequenceTransactionId: params.expense.sequenceTransactionId,
  });
  await recomputeClientPurchasedAmountFromLinkedCharges({
    requisitionId: params.requisition.id,
    actorId: params.actorId || 'system-card-link',
    actorName: params.actorName || 'CRM',
    requisition: params.requisition,
  }).catch(() => undefined);
  return params.requisition.poNumber || params.requisition.prNumber || params.requisition.id;
}

export async function unlinkQbExpenseFromPurchaseOrder(args: {
  expenseId: string;
  purchaseRequisitionId: string;
  actorId?: string;
  actorName?: string;
}): Promise<void> {
  const expenseId = args.expenseId.trim();
  const requisitionId = args.purchaseRequisitionId.trim();
  if (!expenseId || !requisitionId) return;

  const poRef = doc(db, PURCHASE_REQUISITIONS_COLLECTION, requisitionId);
  const expenseRef = doc(db, 'qbExpenses', expenseId);

  await runTransaction(db, async tx => {
    const poSnap = await tx.get(poRef);
    const poData = poSnap.exists() ? poSnap.data() || {} : {};
    const existing = normalizeLinkedExpenseIds(poData.qbExpenseIds, poData.qbExpenseId);
    const nextIds = removeLinkedExpenseId(existing, expenseId);
    const singular =
      typeof poData.qbExpenseId === 'string' && poData.qbExpenseId.trim() === expenseId
        ? nextIds[nextIds.length - 1] || null
        : typeof poData.qbExpenseId === 'string'
          ? poData.qbExpenseId
          : null;

    tx.set(
      expenseRef,
      {
        purchaseRequisitionId: deleteField(),
        poNumber: deleteField(),
        categorizedVia: deleteField(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    tx.set(
      poRef,
      {
        qbExpenseIds: nextIds,
        qbExpenseId: singular,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });

  await recomputeClientPurchasedAmountFromLinkedCharges({
    requisitionId,
    actorId: args.actorId || 'system-card-unlink',
    actorName: args.actorName || 'CRM',
  }).catch(() => undefined);
}
